<template>
  <div class="dashboard-asset-map" :class="{ 'adsol-map': company === 'ADSOL' }">
    <div class="map-container" ref="mapContainer">
      <img 
        :src="backgroundImage" 
        alt="Background" 
        class="background-image" 
        @load="onImageLoad"
        ref="backgroundImg"
      />
      <div
        v-for="tag in displayedTags"
        :key="tag.tag_mac"
        class="tag-marker"
        :class="{ 'tag-moving': animatingTags.includes(tag.tag_mac) }"
        :style="{
          left: `${tag.x}px`,
          top: `${tag.y}px`,
        }"
      >
        {{ tag.devicename }}
      </div>
    </div>
    <!-- company가 OTOKI인 경우 -->
    <template v-if="company === 'OTOKI'">
      <div class="floor-group" :key="'대피소'">
        <h3>대피소 ({{ getFloorTags('대피소').length }})</h3>
        <ul>
          <li v-for="tag in getFloorTags('대피소')" :key="tag.tag_mac">
            {{ tag.devicename }}
          </li>
        </ul>
      </div>
      <div class="floor-group" :key="'내부'">
        <h3>내부 ({{ getFloorTags('내부').length }})</h3>
        <ul>
          <li v-for="tag in getFloorTags('내부')" :key="tag.tag_mac">
            {{ tag.devicename }}
          </li>
        </ul>
      </div>
    </template>
    <!-- company가 SDAA인 경우 -->
    <template v-else-if="company === 'SDAA'">
      <div
        class="floor-group"
        v-for="i in 13"
        :key="'83_' + i"
      >
        <h3>{{ '83_' + i }} ({{ getFloorTags('83_' + i).length }})</h3>
        <ul>
          <li
            v-for="tag in getFloorTags('83_' + i)"
            :key="tag.tag_mac"
          >
            {{ tag.devicename }}
          </li>
        </ul>
      </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios';
import { SNU_GATEWAYS } from '@/constants/snuGateways';

export default {
  name: 'AssetMapComponent',
  data() {
    return {
      floorIndex: {
        '19': 1,
        '20': 0
      },
      groups: [
        { name: '20', tags: [] },
        { name: '19', tags: [] }
      ],
      displayedTags: [],
      animatingTags: [],
      imageLoaded: false,
      containerWidth: 0,
      containerHeight: 0,
      imageNaturalWidth: 0,
      imageNaturalHeight: 0,
      updateInterval: 10, // 10초마다 업데이트
      intervalId: null, // 인터벌 ID
    };
  },
  computed: {
    company() {
      return localStorage.getItem('company') || '';
    },
    backgroundImage() {
      const company = (this.company || '').trim();
      if (company === 'KAVALAN') {
        return require('@/assets/AssetMap2.png');
      } else if (company === 'AmericanWineries') {
        return require('@/assets/AssetMap2.png');
      } else if (company === 'PNK') {
        return require('@/assets/AssetMap_PNK.png');
      } else if (company === 'Wintest') {
        return require('@/assets/AssetMap_Wintest.png');
      } else if (company === 'Skaichips') {
        return require('@/assets/AssetMap_skai.png');
      } else if (company === 'OTOKI') {
        return require('@/assets/AssetMap_OTOKI.png');
      } else if (company === 'SDAA') {
        return require('@/assets/AssetMap_SDAA.png');
      } else if (company === 'SKKU') {
        return require('@/assets/AssetMap_SKKU.png');
      } else if (company === 'SNU') {
        return require('@/assets/AssetMap_SNU.png');
      } else if (company === 'ADSOL') {
        return require('@/assets/AssetMap_ADSOL.png');
      } else {
        return require('@/assets/AssetMap2.png');
      }
    },
    imageScale() {
      if (!this.imageLoaded || !this.containerWidth || !this.containerHeight) {
        return { scaleX: 1, scaleY: 1, offsetX: 0, offsetY: 0 };
      }
      
      const containerRatio = this.containerWidth / this.containerHeight;
      const imageRatio = this.imageNaturalWidth / this.imageNaturalHeight;
      
      let displayedWidth, displayedHeight, offsetX = 0, offsetY = 0;
      
      if (containerRatio > imageRatio) {
        displayedHeight = this.containerHeight;
        displayedWidth = displayedHeight * imageRatio;
        offsetX = (this.containerWidth - displayedWidth) / 2;
      } else {
        displayedWidth = this.containerWidth;
        displayedHeight = displayedWidth / imageRatio;
        offsetY = (this.containerHeight - displayedHeight) / 2;
      }
      
      return {
        scaleX: displayedWidth / this.imageNaturalWidth,
        scaleY: displayedHeight / this.imageNaturalHeight,
        offsetX,
        offsetY
      };
    }
  },
  methods: {
    async fetchTagData() {
      try {
        const company = localStorage.getItem('company');
        const response = await axios.get(`/api/assetMapTagList`, { params: { company } });
        const newTags = Array.isArray(response.data) ? [...response.data] : [];
        
        // 태그를 그룹별로 분류
        const group15Tags = newTags.filter(tag => tag.taglocation === '15');
        const group16Tags = newTags.filter(tag => tag.taglocation === '16');
        const group17Tags = newTags.filter(tag => tag.taglocation === '17');
        const group24Tags = newTags.filter(tag => tag.taglocation === '24');
        const group25Tags = newTags.filter(tag => tag.taglocation === '25');
        const group26Tags = newTags.filter(tag => tag.taglocation === '26');
        const group19Tags = newTags.filter(tag => tag.taglocation === '19');
        const group20Tags = newTags.filter(tag => tag.taglocation === '20');
        const group1Tags = newTags.filter(tag => tag.taglocation === '대피소');
        const group2Tags = newTags.filter(tag => tag.taglocation === '내부');
        // SDAA 83_1~83_13 그룹 추가
        const group83_1Tags = newTags.filter(tag => tag.taglocation === '83_1');
        const group83_2Tags = newTags.filter(tag => tag.taglocation === '83_2');
        const group83_3Tags = newTags.filter(tag => tag.taglocation === '83_3');
        const group83_4Tags = newTags.filter(tag => tag.taglocation === '83_4');
        const group83_5Tags = newTags.filter(tag => tag.taglocation === '83_5');
        const group83_6Tags = newTags.filter(tag => tag.taglocation === '83_6');
        const group83_7Tags = newTags.filter(tag => tag.taglocation === '83_7');
        const group83_8Tags = newTags.filter(tag => tag.taglocation === '83_8');
        const group83_9Tags = newTags.filter(tag => tag.taglocation === '83_9');
        const group83_10Tags = newTags.filter(tag => tag.taglocation === '83_10');
        const group83_11Tags = newTags.filter(tag => tag.taglocation === '83_11');
        const group83_12Tags = newTags.filter(tag => tag.taglocation === '83_12');
        const group83_13Tags = newTags.filter(tag => tag.taglocation === '83_13');
        // SKKU 그룹 추가
        const groupSuperComputingTags = newTags.filter(tag => tag.taglocation === '슈퍼컴퓨팅');
        const group85Tags = newTags.filter(tag => tag.taglocation === '85산학협력단');
        const groupOrganicTags = newTags.filter(tag => tag.taglocation === '유기전처리실');
        const group27ServerTags = newTags.filter(tag => tag.taglocation === '27서버실');
        const groupInorganicTags = newTags.filter(tag => tag.taglocation === '무기분석실');
        const groupPurchaseTags = newTags.filter(tag => tag.taglocation === '구매팀');
        // ADSOL 그룹 추가
        const groupGW01Tags = newTags.filter(tag => tag.taglocation === 'GW 01');
        // company에 따라 그룹 설정
        if (company === 'Wintest') {
          this.groups = [
            { name: '24', tags: group24Tags },
            { name: '25', tags: group25Tags },
            { name: '26', tags: group26Tags }
          ];
        } else if (company === 'KAVALAN') {
          this.groups = [
            { name: '19', tags: group19Tags },
            { name: '20', tags: group20Tags }
          ];
        } else if (company === 'Skaichips') {
          this.groups = [
            { name: '15', tags: group15Tags },
            { name: '16', tags: group16Tags },
            { name: '17', tags: group17Tags }
          ];
        } else if (company === 'OTOKI') {
          this.groups = [
            { name: '대피소', tags: group1Tags },
            { name: '내부', tags: group2Tags }
          ];
        } else if (company === 'SDAA') {
          this.groups = [
            { name: '83_1', tags: group83_1Tags },
            { name: '83_2', tags: group83_2Tags },
            { name: '83_3', tags: group83_3Tags },
            { name: '83_4', tags: group83_4Tags },
            { name: '83_5', tags: group83_5Tags },
            { name: '83_6', tags: group83_6Tags },
            { name: '83_7', tags: group83_7Tags },
            { name: '83_8', tags: group83_8Tags },
            { name: '83_9', tags: group83_9Tags },
            { name: '83_10', tags: group83_10Tags },
            { name: '83_11', tags: group83_11Tags },
            { name: '83_12', tags: group83_12Tags },
            { name: '83_13', tags: group83_13Tags }
          ];
        } else if (company === 'SKKU') {
          this.groups = [
            { name: '슈퍼컴퓨팅', tags: groupSuperComputingTags },
            { name: '85산학협력단', tags: group85Tags },
            { name: '유기전처리실', tags: groupOrganicTags },
            { name: '27서버실', tags: group27ServerTags },
            { name: '무기분석실', tags: groupInorganicTags },
            { name: '구매팀', tags: groupPurchaseTags },
          ];
        } else if (company === 'ADSOL') {
          this.groups = [
            { name: 'GW 01', tags: groupGW01Tags }
          ];
        } else if (company === 'SNU') {
          const entries = [];
          SNU_GATEWAYS.forEach(gw => {
            entries.push({
              name: gw.location,
              tags: newTags.filter(t => t.taglocation === gw.location)
            });
          });
          this.groups = entries;
        } else {
          this.groups = [
            { name: '20', tags: group20Tags },
            { name: '19', tags: group19Tags }
          ];
        }

        // 태그 위치 계산 및 할당
        this.displayedTags = newTags.map(tag => {
          let baseX, baseY;
          let groupIndex;
          
          if (tag.taglocation === '20') {
            groupIndex = group20Tags.findIndex(t => t.tag_mac === tag.tag_mac);
            baseX = 300;
            baseY = 200 + (groupIndex * 100);
          } else if (tag.taglocation === '19') {
            groupIndex = group19Tags.findIndex(t => t.tag_mac === tag.tag_mac);
            baseX = 900;
            baseY = 200 + (groupIndex * 100);
          } else if (tag.taglocation === '24') {
            groupIndex = group24Tags.findIndex(t => t.tag_mac === tag.tag_mac);
            if (groupIndex < 5) {
              baseX = 500;
              baseY = 70 + (groupIndex * 100);
            } else {
              baseX = 600;
              baseY = -180 + (groupIndex * 100);
            }
          } else if (tag.taglocation === '25') {
            groupIndex = group25Tags.findIndex(t => t.tag_mac === tag.tag_mac);
            if (groupIndex < 5) {
              baseX = 800;
              baseY = 70 + (groupIndex * 50);
            } else {
              baseX = 900;
              baseY = -180 + (groupIndex * 50);
            }
          } else if (tag.taglocation === '26') {
            groupIndex = group26Tags.findIndex(t => t.tag_mac === tag.tag_mac);
            if (groupIndex < 5) {
              baseX = 800;
              baseY = 400 + (groupIndex * 50);
            } else {
              baseX = 900;
              baseY = 150 + (groupIndex * 50);
            }
          } else if (tag.taglocation === '15') {
            groupIndex = group15Tags.findIndex(t => t.tag_mac === tag.tag_mac);
            baseX = 200 + Math.floor(groupIndex / 13) * 170;
            baseY = 150 + (groupIndex % 13) * 80;
          } else if (tag.taglocation === '16') {
            groupIndex = group16Tags.findIndex(t => t.tag_mac === tag.tag_mac);
            baseX = 800 + Math.floor(groupIndex / 13) * 170;
            baseY = 150 + (groupIndex % 13) * 80;
          } else if (tag.taglocation === '17') {
            groupIndex = group17Tags.findIndex(t => t.tag_mac === tag.tag_mac);
            baseX = 1500 + Math.floor(groupIndex / 13) * 170;
            baseY = 150 + (groupIndex % 13) * 80;
          } else if (tag.taglocation === '대피소') {
            groupIndex = group1Tags.findIndex(t => t.tag_mac === tag.tag_mac);
            baseX = 600 + Math.floor(groupIndex / 13) * 170;
            baseY = 650 + (groupIndex % 13) * 80;
          } else if (tag.taglocation === '내부') {
            groupIndex = group2Tags.findIndex(t => t.tag_mac === tag.tag_mac);
            baseX = 1000 + Math.floor(groupIndex / 13) * 170;
            baseY = 150 + (groupIndex % 13) * 80;
          } else if (tag.taglocation === '83_1') {
            groupIndex = group83_1Tags.findIndex(t => t.tag_mac === tag.tag_mac);
            baseX = 1000 + Math.floor(groupIndex / 13) * 170;
            baseY = 150 + (groupIndex % 13) * 80;
          } else if (tag.taglocation === '83_2') {
            groupIndex = group83_2Tags.findIndex(t => t.tag_mac === tag.tag_mac);
            baseX = 1000 + Math.floor(groupIndex / 13) * 170;
            baseY = 150 + (groupIndex % 13) * 80;
          } else if (tag.taglocation === '83_3') {
            groupIndex = group83_3Tags.findIndex(t => t.tag_mac === tag.tag_mac);
            baseX = 1000 + Math.floor(groupIndex / 13) * 170;
            baseY = 150 + (groupIndex % 13) * 80;
          } else if (tag.taglocation === '83_4') {
            groupIndex = group83_4Tags.findIndex(t => t.tag_mac === tag.tag_mac);
            baseX = 1000 + Math.floor(groupIndex / 13) * 170;
            baseY = 150 + (groupIndex % 13) * 80;
          } else if (tag.taglocation === '83_5') {
            groupIndex = group83_5Tags.findIndex(t => t.tag_mac === tag.tag_mac);
            baseX = 1000 + Math.floor(groupIndex / 13) * 170;
            baseY = 150 + (groupIndex % 13) * 80;
          } else if (tag.taglocation === '83_6') {
            groupIndex = group83_6Tags.findIndex(t => t.tag_mac === tag.tag_mac);
            baseX = 1000 + Math.floor(groupIndex / 13) * 170;
            baseY = 150 + (groupIndex % 13) * 80;
          } else if (tag.taglocation === '83_7') {
            groupIndex = group83_7Tags.findIndex(t => t.tag_mac === tag.tag_mac);
            baseX = 1000 + Math.floor(groupIndex / 13) * 170;
            baseY = 150 + (groupIndex % 13) * 80;
          } else if (tag.taglocation === '83_8') {
            groupIndex = group83_8Tags.findIndex(t => t.tag_mac === tag.tag_mac);
            baseX = 1000 + Math.floor(groupIndex / 13) * 170;
            baseY = 150 + (groupIndex % 13) * 80;
          } else if (tag.taglocation === '83_9') {
            groupIndex = group83_9Tags.findIndex(t => t.tag_mac === tag.tag_mac);
            baseX = 1000 + Math.floor(groupIndex / 13) * 170;
            baseY = 150 + (groupIndex % 13) * 80;
          } else if (tag.taglocation === '83_10') {
            groupIndex = group83_10Tags.findIndex(t => t.tag_mac === tag.tag_mac);
            baseX = 1000 + Math.floor(groupIndex / 13) * 170;
            baseY = 150 + (groupIndex % 13) * 80;
          } else if (tag.taglocation === '83_11') {
            groupIndex = group83_11Tags.findIndex(t => t.tag_mac === tag.tag_mac);
            baseX = 1000 + Math.floor(groupIndex / 13) * 170;
            baseY = 150 + (groupIndex % 13) * 80;
          } else if (tag.taglocation === '83_12') {
            groupIndex = group83_12Tags.findIndex(t => t.tag_mac === tag.tag_mac);
            baseX = 1000 + Math.floor(groupIndex / 13) * 170;
            baseY = 150 + (groupIndex % 13) * 80;
          } else if (tag.taglocation === '83_13') {
            groupIndex = group83_13Tags.findIndex(t => t.tag_mac === tag.tag_mac);
            baseX = 1000 + Math.floor(groupIndex / 13) * 170;
            baseY = 150 + (groupIndex % 13) * 80;
          } else if (tag.taglocation === '슈퍼컴퓨팅') {
            groupIndex = groupSuperComputingTags.findIndex(t => t.tag_mac === tag.tag_mac);
            baseX = 350 + Math.floor(groupIndex / 4) * 250;
            baseY = 200 + (groupIndex % 4) * 80;
          } else if (tag.taglocation === '85산학협력단') {
            groupIndex = group85Tags.findIndex(t => t.tag_mac === tag.tag_mac);
            baseX = 1200 + Math.floor(groupIndex / 4) * 170;
            baseY = 200 + (groupIndex % 4) * 80;
          } else if (tag.taglocation === '유기전처리실') {
            groupIndex = groupOrganicTags.findIndex(t => t.tag_mac === tag.tag_mac);
            baseX = 2000 + Math.floor(groupIndex / 4) * 170;
            baseY = 200 + (groupIndex % 4) * 80;
          } else if (tag.taglocation === '27서버실') {
            groupIndex = group27ServerTags.findIndex(t => t.tag_mac === tag.tag_mac);
            baseX = 350 + Math.floor(groupIndex / 4) * 250;
            baseY = 700 + (groupIndex % 4) * 80;
          } else if (tag.taglocation === '무기분석실') {
            groupIndex = groupInorganicTags.findIndex(t => t.tag_mac === tag.tag_mac);
            baseX = 1200 + Math.floor(groupIndex / 5) * 170;
            baseY = 700 + (groupIndex % 5) * 80;
          } else if (tag.taglocation === '구매팀') {
            groupIndex = groupPurchaseTags.findIndex(t => t.tag_mac === tag.tag_mac);
            baseX = 1950 + Math.floor(groupIndex / 5) * 170;
            baseY = 700 + (groupIndex % 5) * 80;
          } else if (tag.taglocation === 'GW 01') {
            // ADSOL 회사 'GW 01' 위치
            groupIndex = groupGW01Tags.findIndex(t => t.tag_mac === tag.tag_mac);
            if (groupIndex === -1) {
              groupIndex = 0; // 기본값 설정
            }
            baseX = 400 + Math.floor(groupIndex / 5) * 370;
            baseY = 300 + (groupIndex % 5) * 180;
          } else if (tag.taglocation === '3층 서버실') {
            const grp = this.groups.find(g => g.name === '3층 서버실');
            groupIndex = grp ? grp.tags.findIndex(t => t.tag_mac === tag.tag_mac) : 0;
            if (groupIndex < 0) groupIndex = 0;
            baseX = 230;
            baseY = 210 + (groupIndex * 78);
          } else if (tag.taglocation === '3층 2회의실') {
            const grp = this.groups.find(g => g.name === '3층 2회의실');
            groupIndex = grp ? grp.tags.findIndex(t => t.tag_mac === tag.tag_mac) : 0;
            if (groupIndex < 0) groupIndex = 0;
            baseX = 1040;
            baseY = 210 + (groupIndex * 78);
          } else if (tag.taglocation === '3층 중앙 실험실') {
            const grp = this.groups.find(g => g.name === '3층 중앙 실험실');
            groupIndex = grp ? grp.tags.findIndex(t => t.tag_mac === tag.tag_mac) : 0;
            if (groupIndex < 0) groupIndex = 0;
            baseX = 1740;
            baseY = 210 + (groupIndex * 78);
          }

          // baseX, baseY가 설정되지 않았을 경우 기본값 설정
          if (baseX === undefined || baseY === undefined) {
            baseX = 0;
            baseY = 0;
          }

          // 스케일을 적용한 실제 좌표 계산
          const scale = this.imageScale;
          const x = baseX * scale.scaleX + scale.offsetX;
          const y = baseY * scale.scaleY + scale.offsetY;

          return { ...tag, x, y, baseX, baseY };
        });

      } catch (error) {
        console.error('Error fetching tag data:', error);
      }
    },

    onImageLoad(event) {
      this.imageNaturalWidth = event.target.naturalWidth;
      this.imageNaturalHeight = event.target.naturalHeight;
      this.imageLoaded = true;
      this.updateContainerSize();
      this.recalculateTagPositions();
    },

    updateContainerSize() {
      this.$nextTick(() => {
        const container = this.$refs.mapContainer;
        if (container) {
          this.containerWidth = container.clientWidth;
          this.containerHeight = container.clientHeight;
        }
      });
    },

    onResize() {
      this.updateContainerSize();
      setTimeout(() => {
        this.recalculateTagPositions();
      }, 100);
    },

    recalculateTagPositions() {
      if (!this.imageLoaded || this.displayedTags.length === 0) return;
      
      const scale = this.imageScale;
      this.displayedTags = this.displayedTags.map(tag => {
        if (tag.baseX !== undefined && tag.baseY !== undefined) {
          const x = tag.baseX * scale.scaleX + scale.offsetX;
          const y = tag.baseY * scale.scaleY + scale.offsetY;
          return { ...tag, x, y };
        }
        return tag;
      });
    },

    startFetching() {
      // 즉시 한번 실행
      this.fetchTagData();
      
      // 기존 인터벌이 있으면 제거
      if (this.intervalId) {
        clearInterval(this.intervalId);
      }
      
      // 새로운 인터벌 설정
      this.intervalId = setInterval(() => {
        this.fetchTagData();
      }, this.updateInterval * 1000);
    },

    getFloorTags(floor) {
      return this.displayedTags.filter(tag => tag.taglocation === floor);
    }
  },
  mounted() {
    this.startFetching(); // fetchTagData() 대신 startFetching() 호출
    window.addEventListener('resize', this.onResize);
    this.updateContainerSize();
  },
  beforeUnmount() {
    // 인터벌 정리
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
    window.removeEventListener('resize', this.onResize);
  }
};
</script>

<style scoped>
.dashboard-asset-map {
  width: 100%;
  height: 100%;
  /* 하단 카드(19,20)의 순서를 CSS로 제어하기 위해 플렉스 컨테이너로 전환 */
  display: flex;
  flex-direction: column;
}

.map-container {
  position: relative;
  width: 100%;
  height: 320px; /* 350px에서 320px로 조정 */
  overflow: hidden;
  border-radius: 8px;
  /* 맵은 항상 최상단에 고정 */
  order: 0;
}

/* ADSOL 회사일 때 배경 이미지가 카드 크기에 맞게 축소되도록 */
.dashboard-asset-map.adsol-map .map-container {
  height: 320px; /* 원래 높이 유지 */
}

.dashboard-asset-map.adsol-map .background-image {
  object-fit: contain; /* contain으로 변경하여 카드 크기에 맞게 축소 */
}

.background-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

/* 하단 카드(그룹) 순서 조정: 19 -> 20 에서 20 -> 19 로 변경 */
.dashboard-asset-map > .floor-group {
  order: 1; /* 기본 순서 */
}
/* 첫 번째로 렌더된 그룹을 두 번째로 밀고 */
.dashboard-asset-map > .floor-group:nth-of-type(1) {
  order: 2;
}
/* 두 번째로 렌더된 그룹을 첫 번째로 당김 */
.dashboard-asset-map > .floor-group:nth-of-type(2) {
  order: 1;
}

.tag-marker {
  position: absolute;
  transform: translate(-50%, -50%);
  padding: 6px 10px; /* 대시보드용으로 조금 더 작게 */
  border-radius: 4px;
  background-color: rgba(0, 238, 255, 0.9);
  color: black;
  font-size: 12px; /* 폰트 크기도 조금 작게 */
  font-weight: bold;
  white-space: nowrap;
  z-index: 1000;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  border: 1px solid rgba(0,0,0,0.1);
  transition: all 0.3s ease;
}

.tag-moving {
  background-color: rgba(255, 165, 0, 0.9);
  transform: translate(-50%, -50%) scale(1.1);
  box-shadow: 0 4px 8px rgba(0,0,0,0.3);
  z-index: 1001;
}

.tag-marker:hover {
  background-color: rgba(0, 200, 255, 0.9);
  transform: translate(-50%, -50%) scale(1.1);
  z-index: 1002;
}
</style> 